import { Chart } from 'chart.js/auto';
import store from '../../../../state';
import { formatElevation } from '../../../../utils/unitConversion';
import { ForecastWidgetConfig, WidgetRenderContext, WidgetRenderer } from '../types';
import { ForecastBinaryService } from '../../../../services/ForecastBinaryService';
import TierAccessService from '../../../../services/TierAccessService';

export class ElevationTempWidget implements WidgetRenderer {
  public renderHtml(widget: ForecastWidgetConfig, _ctx: WidgetRenderContext): string {
    const title = widget.title || 'Elevation Temperatures & Lapse Trend';
    const height = widget.options?.chartHeight || 160;
    const canvasId = `poi-elev-canvas-${widget.id}`;

    return `
      <div class="metric-card" style="background: rgba(30, 41, 59, 0.75); border: 1px solid rgba(255, 255, 255, 0.08); border-radius: 14px; padding: 14px 16px; box-shadow: 0 8px 24px rgba(0,0,0,0.3);">
        <div style="display: flex; justify-content: space-between; align-items: center; margin-bottom: 6px;">
          <span style="font-size: 0.95rem; font-weight: 700; color: #fff;">${title}</span>
        </div>
        <div style="height: ${height}px; position: relative; width: 100%; margin-top: 4px;">
          <canvas id="${canvasId}"></canvas>
        </div>
      </div>
    `;
  }

  public init(widget: ForecastWidgetConfig, ctx: WidgetRenderContext): void {
    const canvasId = `poi-elev-canvas-${widget.id}`;
    const canvas = document.getElementById(canvasId) as HTMLCanvasElement;
    if (!canvas) return;

    const data = ctx.data;
    const isImperial = store.getState().units === 'imperial';
    const units = isImperial ? 'imperial' : 'metric';
    const tempUnit = isImperial ? '°F' : '°C';
    const labels = data.timeLabels || [];

    if (!labels || labels.length === 0) {
      const parent = canvas.parentElement;
      if (parent) {
        canvas.style.display = 'none';
        parent.innerHTML = TierAccessService.buildTrendChartDenialHtml({
          denial: data?.accessDenial || ForecastBinaryService.lastAccessDenial,
          requiredTier: 'pro',
          featureLabel: `${widget.title || 'elevation temperature & lapse'} trend charts`
        });
      }
      return;
    }
    const convT = (arr: number[] | undefined) => {
      if (!arr) return [];
      return isImperial ? arr : arr.map(f => Math.round((f - 32) * 5 / 9));
    };

    const datasets: any[] = [];

    if (data.tempsHighest && data.tempsHighest.length) {
      datasets.push({
        label: `Summit (${formatElevation(data.maxElev, units)})`,
        data: convT(data.tempsHighest),
        borderColor: '#a855f7',
        backgroundColor: 'transparent',
        borderWidth: 2,
        tension: 0.3,
        pointRadius: 0
      });
    }

    if (data.tempsMid && data.tempsMid.length) {
      datasets.push({
        label: `Mid-Mtn (${formatElevation((data.maxElev + data.minElev) / 2, units)})`,
        data: convT(data.tempsMid),
        borderColor: '#fbbf24',
        backgroundColor: 'transparent',
        borderWidth: 2,
        tension: 0.3,
        pointRadius: 0
      });
    }

    const poiSeries = (data.tempsPoi && data.tempsPoi.length) ? data.tempsPoi : data.tempsForecast;
    if (poiSeries && poiSeries.length) {
      datasets.push({
        label: `Point (${formatElevation(data.poiElev || data.forecastElev, units)})`,
        data: convT(poiSeries),
        borderColor: '#38bdf8',
        backgroundColor: 'transparent',
        borderWidth: 2.5,
        tension: 0.3,
        pointRadius: 0
      });
    }

    if (data.tempsLowest && data.tempsLowest.length) {
      datasets.push({
        label: `Base (${formatElevation(data.minElev, units)})`,
        data: convT(data.tempsLowest),
        borderColor: '#10b981',
        backgroundColor: 'transparent',
        borderWidth: 2,
        tension: 0.3,
        pointRadius: 0
      });
    }

    if (widget.options?.showFreezingLine !== false) {
      const freezingVal = isImperial ? 32 : 0;
      datasets.push({
        label: isImperial ? '32°F Freezing' : '0°C Freezing',
        data: new Array(labels.length).fill(freezingVal),
        borderColor: 'rgba(103, 232, 249, 0.7)',
        borderDash: [5, 4],
        borderWidth: 1.5,
        pointRadius: 0,
        tension: 0
      });
    }

    const chart = new Chart(canvas, {
      type: 'line',
      data: {
        labels,
        datasets
      },
      options: {
        responsive: true,
        maintainAspectRatio: false,
        interaction: {
          mode: 'index',
          intersect: false
        },
        plugins: {
          legend: {
            display: true,
            position: 'top',
            labels: {
              color: '#94a3b8',
              font: { size: 10, family: 'Inter' },
              boxWidth: 8,
              boxHeight: 8,
              padding: 10
            }
          },
          tooltip: {
            backgroundColor: 'rgba(15, 23, 42, 0.95)',
            borderColor: 'rgba(255, 255, 255, 0.1)',
            borderWidth: 1,
            callbacks: {
              label: (item) => `${item.dataset.label}: ${item.raw}${tempUnit}`
            }
          }
        },
        scales: {
          x: {
            grid: { color: 'rgba(255, 255, 255, 0.05)' },
            ticks: {
              color: '#64748b',
              font: { size: 9 },
              maxTicksLimit: 8
            }
          },
          y: {
            grid: { color: 'rgba(255, 255, 255, 0.05)' },
            ticks: {
              color: '#94a3b8',
              font: { size: 9 },
              callback: (val) => `${val}°`
            }
          }
        }
      }
    });

    ctx.charts.push(chart);
  }
}
